import React, { useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import ApiService from '../../services/api.service';

const FS = '0.8125rem';
const FIELD_SX = { '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: FS }, '& .MuiFormHelperText-root': { fontSize: '0.6875rem' } };
const EMPTY = { current: '', next: '', confirm: '' };

/** Portal user changes their own password (POST /api/portal/auth/change-password). */
const PortalChangePasswordDialog = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const close = () => {
    if (saving) return;
    setForm(EMPTY);
    setError(null);
    setDone(false);
    onClose();
  };

  const mismatch = form.confirm.length > 0 && form.next !== form.confirm;
  const valid = form.current && form.next.length >= 6 && form.next === form.confirm;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await ApiService.changePortalPassword(form.current, form.next);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Could not change the password');
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Change password</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 1.5, ...FIELD_SX }}>
        {error && <Alert severity="error" sx={{ fontSize: FS }}>{error}</Alert>}
        {done ? (
          <Alert severity="success" sx={{ fontSize: FS }}>Password changed. Use it the next time you sign in.</Alert>
        ) : (
          <>
            <TextField size="small" type="password" label="Current password" value={form.current} onChange={set('current')} autoFocus />
            <TextField size="small" type="password" label="New password" value={form.next} onChange={set('next')} helperText="Minimum 6 characters" />
            <TextField
              size="small" type="password" label="Confirm new password" value={form.confirm} onChange={set('confirm')}
              error={mismatch} helperText={mismatch ? 'Does not match the new password' : ' '}
              onKeyDown={(e) => { if (e.key === 'Enter' && valid) save(); }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={close} disabled={saving} sx={{ textTransform: 'none', fontSize: FS }}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button variant="contained" onClick={save} disabled={!valid || saving} sx={{ textTransform: 'none', fontSize: FS }}>
            {saving ? <CircularProgress size={16} color="inherit" /> : 'Change password'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PortalChangePasswordDialog;
